import { sportLabel } from "@/lib/conditions/sport-meta";
import type {
  CompanionCrossSportResult,
  CompanionDiscoveryRecommendation,
  CompanionRankedRecommendation,
  CompanionRecommendationCandidate,
  CompanionRecommendationExplanation,
  CompanionRecommendationSport,
  CompanionSportEvidence,
  CompanionRecommendationEmptyReason,
} from "./recommendation-types";

const DIRECT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const DISCOVERY_MAX_AGE_MS = 6 * 60 * 60 * 1000;
const DISCOVERY_MIN_SCORE = 7;
const DISCOVERY_SCORE_GAP = 0.8;
const DISTINCT_WINDOW_GAP_MS = 60 * 60 * 1000;

const EXPERIENCED_DISCOVERY_SPORTS = new Set<CompanionRecommendationSport>([
  "off-piste",
  "touring",
  "downwind-foil",
  "sup-downwind",
  "diving",
]);

export type CrossSportRecommendationInput = {
  candidates: CompanionRecommendationCandidate[];
  evidence: CompanionSportEvidence[];
  now: Date;
  hasBase?: boolean;
};

export function buildCrossSportRecommendations(
  input: CrossSportRecommendationInput,
): CompanionCrossSportResult {
  const knownSports = new Set(input.evidence.map((item) => item.sportKey));
  const candidates = input.candidates.map((candidate) => {
    const ability = input.evidence.find((item) => item.sportKey === candidate.sportKey)?.ability;
    if (candidate.beginnerSuitability !== "experienced-only") return candidate;
    if (ability === "beginner") return { ...candidate, abilityCompatibility: "incompatible" as const };
    if (candidate.abilityCompatibility === "unknown" && (ability === "advanced" || ability === "expert")) return { ...candidate, abilityCompatibility: "compatible" as const };
    return candidate;
  });
  const knownCandidates = candidates
    .filter((candidate) => knownSports.has(candidate.sportKey))
    .filter((candidate) => isEligibleDirectCandidate(candidate, input.now))
    .map(rankKnownCandidate)
    .sort(compareKnownRecommendations);
  const bestForYou = knownCandidates[0] ?? null;

  const discoveryCandidates = input.candidates
    .filter((candidate) => !knownSports.has(candidate.sportKey))
    .filter((candidate) => isEligibleDiscoveryCandidate(candidate, input.now))
    .filter((candidate) => isMeaningfulDiscovery(candidate, bestForYou))
    .sort(compareDiscoveryCandidates);
  const discoveryCandidate = discoveryCandidates[0] ?? null;

  return {
    bestForYou,
    emptyReason: bestForYou ? null : emptyReason(input, candidates, knownSports),
    alsoFiringNearby: discoveryCandidate
      ? buildDiscoveryRecommendation(discoveryCandidate)
      : null,
  };
}

function emptyReason(
  input: CrossSportRecommendationInput,
  candidates: CompanionRecommendationCandidate[],
  knownSports: Set<CompanionRecommendationSport>,
): CompanionRecommendationEmptyReason {
  if (knownSports.size === 0) return "no-sports";
  if (input.hasBase === false) return "no-base";
  let remaining = candidates.filter((candidate) => knownSports.has(candidate.sportKey));
  if (!remaining.length) return "missing-data";
  const checks: [CompanionRecommendationEmptyReason, (candidate: CompanionRecommendationCandidate) => boolean][] = [
    ["stale-data", (candidate) => isFreshWithin(candidate.updatedAt, input.now, DIRECT_MAX_AGE_MS)],
    ["safety", (candidate) => candidate.safetyState !== "flagged-red"],
    ["safety-unknown", (candidate) => candidate.safetyState !== "no-data"],
    ["ability", confirmedAbility],
    ["travel", (candidate) => candidate.withinTravelCap],
    ["availability", (candidate) => candidate.fitsAvailability !== false],
    ["no-window", (candidate) => hasUsableWindow(candidate, input.now)],
  ];
  for (const [reason, passes] of checks) {
    remaining = remaining.filter(passes);
    if (!remaining.length) return reason;
  }
  return "missing-data";
}

function isEligibleDirectCandidate(
  candidate: CompanionRecommendationCandidate,
  now: Date,
): boolean {
  if (!Number.isFinite(candidate.score)) return false;
  if (candidate.safetyState === "flagged-red" || candidate.safetyState === "no-data") return false;
  if (!confirmedAbility(candidate)) return false;
  if (!candidate.withinTravelCap || candidate.fitsAvailability === false) {
    return false;
  }
  if (!hasUsableWindow(candidate, now)) return false;
  return isFreshWithin(candidate.updatedAt, now, DIRECT_MAX_AGE_MS);
}

function isEligibleDiscoveryCandidate(
  candidate: CompanionRecommendationCandidate,
  now: Date,
): boolean {
  if (!Number.isFinite(candidate.score) || candidate.score < DISCOVERY_MIN_SCORE) {
    return false;
  }
  if (candidate.safetyState !== "clear") return false;
  if (candidate.beginnerSuitability === "safety-restricted") return false;
  if (candidate.abilityCompatibility === "incompatible") return false;
  if (!candidate.withinTravelCap || candidate.fitsAvailability === false) {
    return false;
  }
  if (!hasUsableWindow(candidate, now)) return false;
  return isFreshWithin(candidate.updatedAt, now, DISCOVERY_MAX_AGE_MS);
}

function hasUsableWindow(
  candidate: CompanionRecommendationCandidate,
  now: Date,
): boolean {
  const arrival = now.getTime() + Math.max(0, candidate.driveMinutes) * 60_000;
  const remainingStart = timestamp(candidate.remainingWindowStartAt);
  const remainingEnd = timestamp(candidate.remainingWindowEndAt);
  const hasRemaining = Boolean(
    candidate.remainingWindow && remainingStart !== null && remainingStart <= now.getTime() &&
      remainingEnd !== null && remainingEnd > arrival,
  );
  const nextStart = timestamp(candidate.nextWindowStartAt);
  const nextEnd = timestamp(candidate.nextWindowEndAt);
  const hasNext = Boolean(
    candidate.nextWindow && nextStart !== null && nextStart >= now.getTime() &&
      nextEnd !== null && nextEnd > Math.max(nextStart, arrival),
  );
  return hasRemaining || hasNext;
}

function isFreshWithin(
  updatedAt: string | null,
  now: Date,
  maxAgeMs: number,
): boolean {
  const updatedTime = timestamp(updatedAt);
  if (updatedTime === null) return false;
  const age = now.getTime() - updatedTime;
  return age >= 0 && age <= maxAgeMs;
}

function rankKnownCandidate(
  candidate: CompanionRecommendationCandidate,
): CompanionRankedRecommendation {
  const rankScore =
    candidate.score * 10 +
    currentWindowPoints(candidate) +
    availabilityPoints(candidate) +
    confidencePoints(candidate) +
    practicalAccessPoints(candidate) +
    supportPoints(candidate) -
    Math.min(9, candidate.driveMinutes / 10);

  return {
    candidate,
    explanation: recommendationExplanation(candidate),
    rankScore,
  };
}

function currentWindowPoints(candidate: CompanionRecommendationCandidate): number {
  if (candidate.remainingWindow) return 12;
  if (candidate.nextWindow) return 6;
  return 0;
}

function availabilityPoints(candidate: CompanionRecommendationCandidate): number {
  return candidate.fitsAvailability === true ? 8 : 0;
}

function confidencePoints(candidate: CompanionRecommendationCandidate): number {
  if (candidate.confidence === "high") return 4;
  if (candidate.confidence === "medium") return 2;
  return 0;
}

function practicalAccessPoints(candidate: CompanionRecommendationCandidate): number {
  return (candidate.plannerHref ? 2 : 0) + (candidate.isSaved ? 1 : 0);
}

function supportPoints(candidate: CompanionRecommendationCandidate): number {
  return (candidate.callSupported ? 4 : 0) +
    (candidate.briefingSupported ? 2 : 0);
}

function compareKnownRecommendations(
  left: CompanionRankedRecommendation,
  right: CompanionRankedRecommendation,
): number {
  return (
    right.rankScore - left.rankScore ||
    right.candidate.score - left.candidate.score ||
    left.candidate.driveMinutes - right.candidate.driveMinutes ||
    left.candidate.id.localeCompare(right.candidate.id)
  );
}

function isMeaningfulDiscovery(
  candidate: CompanionRecommendationCandidate,
  bestForYou: CompanionRankedRecommendation | null,
): boolean {
  if (!bestForYou) return true;
  if (
    candidate.score - bestForYou.candidate.score >=
    DISCOVERY_SCORE_GAP - 1e-9
  ) {
    return true;
  }
  return bestForYou.candidate.score < DISCOVERY_MIN_SCORE &&
    hasDistinctWindow(candidate, bestForYou.candidate);
}

function hasDistinctWindow(
  candidate: CompanionRecommendationCandidate,
  known: CompanionRecommendationCandidate,
): boolean {
  if (candidate.remainingWindow && !known.remainingWindow) return true;

  const candidateStart = timestamp(candidate.nextWindowStartAt);
  const knownEnd = timestamp(known.remainingWindowEndAt);
  return candidateStart !== null && knownEnd !== null &&
    candidateStart - knownEnd >= DISTINCT_WINDOW_GAP_MS;
}

function compareDiscoveryCandidates(
  left: CompanionRecommendationCandidate,
  right: CompanionRecommendationCandidate,
): number {
  return (
    right.score - left.score ||
    Number(Boolean(right.remainingWindow)) - Number(Boolean(left.remainingWindow)) ||
    confidenceRank(right.confidence) - confidenceRank(left.confidence) ||
    left.driveMinutes - right.driveMinutes ||
    left.id.localeCompare(right.id)
  );
}

function confidenceRank(value: CompanionRecommendationCandidate["confidence"]): number {
  if (value === "high") return 2;
  if (value === "medium") return 1;
  return 0;
}

function buildDiscoveryRecommendation(
  candidate: CompanionRecommendationCandidate,
): CompanionDiscoveryRecommendation {
  return {
    candidate,
    explanation: {
      ...recommendationExplanation(candidate),
      whySport: `${sportLabel(candidate.sportKey)} has a strong nearby forecast. It is not one of your saved sports yet.`,
    },
    rankScore: candidate.score * 10,
    suitability: discoverySuitability(candidate),
  };
}

function discoverySuitability(
  candidate: CompanionRecommendationCandidate,
): CompanionDiscoveryRecommendation["suitability"] {
  if (EXPERIENCED_DISCOVERY_SPORTS.has(candidate.sportKey)) {
    return "experienced-only";
  }
  return candidate.beginnerSuitability === "safety-restricted"
    ? "unknown"
    : candidate.beginnerSuitability;
}

function recommendationExplanation(
  candidate: CompanionRecommendationCandidate,
): CompanionRecommendationExplanation {
  const label = sportLabel(candidate.sportKey);
  return {
    whySport: `${label} has the strongest practical score among your known sports.`,
    whyPlace: candidate.isSaved
      ? `${candidate.placeName} is saved and about ${candidate.driveMinutes} minutes away.`
      : `${candidate.placeName} is about ${candidate.driveMinutes} minutes away.`,
    whyTime: candidate.remainingWindow
      ? `${candidate.remainingWindow} is still usable.`
      : `${candidate.nextWindow ?? "No later window"} is the next usable window.`,
    whatCouldChange:
      candidate.verdict ??
      candidate.safetyNotes[0] ??
      "No stored change signal is available.",
  };
}

function timestamp(value: string | null): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function confirmedAbility(candidate: CompanionRecommendationCandidate): boolean {
  return candidate.abilityCompatibility !== "incompatible" && candidate.beginnerSuitability !== "safety-restricted"
    && (candidate.beginnerSuitability !== "experienced-only" || candidate.abilityCompatibility === "compatible");
}
